import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { CommonActions, useNavigation } from '@react-navigation/native';
import {
  AcceptMissionStatus,
  AgeRestrictionType,
  GetMissionReferralAcceptabilityResponse,
  GetMissionReferralResponse,
  MissionReferralStatus,
  MissionsClient,
} from '../api/api.g';
import Label from '../components/common/Label';
import Loading from '../components/common/Loading';
import MissionReferralPopup from '../components/onboarding/MissionReferralPopup';
import MissionReferralMessage from '../components/onboarding/MissionReferralMessage';
import useMissionReferrals, { missionReferralRegistry, referralListing } from '../hooks/mission-referrals-hook';
import ModalContainer from '../components/common/ModalContainer';
import Login from './Login';
import useApi from '../hooks/api-hook';
import useShopperBootstrap from '../hooks/shopper-bootstrap-hook';
import { useAppDispatch, useAppSelector } from '../hooks/store-hook';
import useCustomTheme from '../hooks/theme-hook';
import useTranslation from '../hooks/translation-hook';
import { LoggedState } from '../models/AuthModelsCommon';
import { MISSION_REFERRAL_SIGN_UP_SOURCE } from '../models/SignUpSources';
import { authActions } from '../models/helpers/platformImportHelper';
import { rootNavigationRef } from '../navigation/rootNavigation';
import { lightBoxActions } from '../store/lightBoxSlice';
import { missionsActions } from '../store/missionsSlice';
import { isLinkedMysteryShopperRotationLimitReached } from '../models/helpers/linkedMysteryShopperRotationStatus';
import { isReferralRelatedMinorRequired } from '../helpers/referralEligibility';

interface ReferralMissionMapTarget {
  token: string;
  branchId: string;
  periodMissionConfigurationId: string;
  latitude: number;
  longitude: number;
}

const getFirstName = (name?: string) => {
  const trimmedName = name?.trim();

  if (!trimmedName) {
    return undefined;
  }

  return trimmedName.split(/\s+/)[0];
};

const isMissionReferralAccepted = (referral?: GetMissionReferralResponse) =>
  referral?.isAccepted === true || referral?.status === MissionReferralStatus.InvitationAccepted;

const getAgeRestrictionReferralMessage = (ageRestrictionType?: AgeRestrictionType) => {
  switch (ageRestrictionType) {
    case AgeRestrictionType.Minor:
      return {
        titleKey: 'mission-referral-related-minor-required-title',
        bodyKey: 'RelatedMinorRequiredForMission',
      };
    case AgeRestrictionType.Adult:
      return {
        titleKey: 'mission-referral-age-restriction-required-title',
        bodyKey: 'AgeRestrictionRequiredForMission',
      };
    default:
      return undefined;
  }
};

const MissionReferralAcceptance = ({ route }) => {
  const apiMissions = useApi(MissionsClient);
  const authStore = useAppSelector((selector) => selector.auth);
  const shopperBootstrap = useShopperBootstrap();
  const { key: referralScope } = useMissionReferrals();
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const t = useTranslation();
  const theme = useCustomTheme();
  const referralToken = typeof route?.params?.token === 'string' ? route.params.token.trim() : '';
  const [processing, setProcessing] = useState(true);
  const [showReferralLogin, setShowReferralLogin] = useState(false);
  const [referralAvailabilityChecked, setReferralAvailabilityChecked] = useState(false);
  const [referralLoginFirstName, setReferralLoginFirstName] = useState<string | undefined>();
  const loadedReferralTokenRef = useRef<string | undefined>(undefined);
  const loadedAcceptabilityTokenRef = useRef<string | undefined>(undefined);
  const loadedAcceptabilityAuthTokenRef = useRef<string | undefined>(undefined);
  const publicAvailabilityTokenRef = useRef<string | undefined>(undefined);
  const processedTokenRef = useRef<string | undefined>(undefined);
  const currentReferralRef = useRef<GetMissionReferralResponse | undefined>(undefined);
  const currentAcceptabilityRef = useRef<GetMissionReferralAcceptabilityResponse | undefined>(undefined);
  const hasSignedInUser = !!authStore.user && !authStore.user.isAnonymous;
  const isAuthenticatedReferralUser = authStore.logged === LoggedState.Logged && !!authStore.idToken;
  const authenticatedReferralToken = isAuthenticatedReferralUser ? authStore.idToken : undefined;
  const canUsePublicReferralCheck = authStore.logged === LoggedState.NotLogged && !hasSignedInUser && !authStore.idToken;
  const canUsePublicReferralCheckRef = useRef(canUsePublicReferralCheck);

  const loadingBackgroundColor = theme.background?.primary?.toString?.() ?? '#ffffff';
  const referralLoginRoute = useMemo(
    () => ({
      name: 'referralLogin',
      params: {
        referralToken,
        referrerFirstName: referralLoginFirstName,
        referralAvailabilityChecked: referralAvailabilityChecked ? 'true' : undefined,
        newLogin: 'true',
      },
    }),
    [referralToken, referralAvailabilityChecked, referralLoginFirstName]
  );

  useEffect(() => {
    canUsePublicReferralCheckRef.current = canUsePublicReferralCheck;
  }, [canUsePublicReferralCheck]);

  const closeLightBox = useCallback(() => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
  }, [dispatch]);

  const resetToMap = useCallback((params?: Record<string, string>) => {
    const mapRoute = params ? { name: 'map', params } : { name: 'map' };

    if (rootNavigationRef.isReady()) {
      rootNavigationRef.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [mapRoute],
        })
      );
      return;
    }

    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [mapRoute],
      })
    );
  }, [navigation]);

  const clearReferralNavigationState = useCallback(() => {
    dispatch(authActions.setParams(undefined));
    dispatch(authActions.setReturnPath('map'));
  }, [dispatch]);

  const completeReferralFlowToMap = useCallback(() => {
    dispatch(missionsActions.setMissionReferralMapTarget(undefined));
    clearReferralNavigationState();
    resetToMap();
  }, [clearReferralNavigationState, dispatch, resetToMap]);

  const goToMap = useCallback(() => {
    closeLightBox();
    completeReferralFlowToMap();
  }, [closeLightBox, completeReferralFlowToMap]);

  const goToReferralMission = useCallback(
    (target: ReferralMissionMapTarget) => {
      closeLightBox();
      clearReferralNavigationState();
      dispatch(missionsActions.setResetMap(false));
      dispatch(
        missionsActions.setMissionReferralMapTarget({
          token: target.token,
          branchId: target.branchId,
          periodMissionConfigurationId: target.periodMissionConfigurationId,
        })
      );
      resetToMap({
        pos: target.branchId,
        location: `${target.latitude}|${target.longitude}`,
      });
    },
    [clearReferralNavigationState, closeLightBox, dispatch, resetToMap]
  );

  const showReferralMessage = useCallback(
    (titleKey: string, bodyKey: string) => {
      setProcessing(false);
      clearReferralNavigationState();
      dispatch(
        lightBoxActions.setContent(
          <MissionReferralMessage titleKey={titleKey} bodyKey={bodyKey} onClose={goToMap} />
        )
      );
      dispatch(lightBoxActions.setVisibility(true));
    },
    [clearReferralNavigationState, dispatch, goToMap]
  );

  const showUnavailableReferralMessage = useCallback(() => {
    showReferralMessage('mission-referral-unavailable-title', 'mission-referral-unavailable-body');
  }, [showReferralMessage]);

  const showAgeRestrictionReferralMessage = useCallback(
    (ageRestrictionType?: AgeRestrictionType) => {
      const message = getAgeRestrictionReferralMessage(ageRestrictionType);

      if (!message) {
        return false;
      }

      showReferralMessage(message.titleKey, message.bodyKey);
      return true;
    },
    [showReferralMessage]
  );

  const showAlreadyAcceptedReferralMessage = useCallback(() => {
    setProcessing(false);
    clearReferralNavigationState();
    dispatch(
      lightBoxActions.setContent(
        <ModalContainer title={t('Attention')} onConfirm={goToMap} onClose={goToMap}>
          <View>
            <Label style={{ fontSize: 16, lineHeight: 22, textAlign: 'center' }}>{t('MissionAlreadyInProgress')}</Label>
          </View>
        </ModalContainer>
      )
    );
    dispatch(lightBoxActions.setVisibility(true));
  }, [clearReferralNavigationState, dispatch, goToMap, t]);

  const getReferralDetails = useCallback(
    async (token: string, forceRefresh = false) => {
      if (!forceRefresh && loadedReferralTokenRef.current === token && currentReferralRef.current) {
        return currentReferralRef.current;
      }

      apiMissions.config.accessToken = () => Promise.resolve('');
      const referral = await apiMissions.getMissionReferral(token, apiMissions.tokenSource?.token);
      currentReferralRef.current = referral;
      loadedReferralTokenRef.current = token;

      return referral;
    },
    [apiMissions]
  );

  const getReferralAcceptability = useCallback(
    async (token: string, forceRefresh = false, authToken?: string) => {
      if (!authToken) {
        return undefined;
      }

      if (
        !forceRefresh &&
        loadedAcceptabilityTokenRef.current === token &&
        loadedAcceptabilityAuthTokenRef.current === authToken &&
        currentAcceptabilityRef.current
      ) {
        return currentAcceptabilityRef.current;
      }

      apiMissions.config.accessToken = () => Promise.resolve(authToken);
      const acceptability = await apiMissions.getMissionReferralAcceptability(token, apiMissions.tokenSource?.token);
      currentAcceptabilityRef.current = acceptability;
      loadedAcceptabilityTokenRef.current = token;
      loadedAcceptabilityAuthTokenRef.current = authToken;

      return acceptability;
    },
    [apiMissions]
  );

  const showReferralPopup = useCallback(
    (target: ReferralMissionMapTarget, referrerFirstName?: string) => {
      setProcessing(false);
      clearReferralNavigationState();
      dispatch(missionsActions.setMissionReferralMapTarget(undefined));

      dispatch(
        lightBoxActions.setContent(
          <MissionReferralPopup referrerFirstName={referrerFirstName} onClose={goToMap} onViewMission={() => goToReferralMission(target)} />
        )
      );
      dispatch(lightBoxActions.setVisibility(true));
    },
    [clearReferralNavigationState, dispatch, goToMap, goToReferralMission]
  );

  const runReferralFlow = useCallback(
    async (token: string, authToken?: string) => {
      if (!authToken) {
        return;
      }

      setProcessing(true);

      try {
        const referral = await getReferralDetails(token, true);

        if (isMissionReferralAccepted(referral)) {
          if (referralScope) await missionReferralRegistry.remove(referralScope, token);
          showAlreadyAcceptedReferralMessage();
          return;
        }

        if (!referral || referral.canAccept !== true || referral.isExpired === true || referral.status === MissionReferralStatus.InvitationCanceled) {
          if (referralScope) await missionReferralRegistry.remove(referralScope, token);
          showUnavailableReferralMessage();
          return;
        }

        const acceptability = await getReferralAcceptability(token, true, authToken);

        if (!acceptability) {
          showUnavailableReferralMessage();
          return;
        }

        if (acceptability.acceptMissionStatus === AcceptMissionStatus.MissionAlreadyInProgress) {
          showReferralMessage('mission-referral-already-assigned-title', 'mission-referral-already-assigned-body');
          return;
        }

        if (isLinkedMysteryShopperRotationLimitReached(acceptability.rotationInfo)) {
          showReferralMessage('Attention', 'linked-mystery-shopper-rotation-limit-reached');
          return;
        }

        const deferRelatedMinorRequirement = isReferralRelatedMinorRequired(acceptability);
        if (
          acceptability.acceptMissionStatus === AcceptMissionStatus.UserIneligibleForMission &&
          !deferRelatedMinorRequirement &&
          showAgeRestrictionReferralMessage(acceptability.ageRestrictionType)
        ) {
          return;
        }

        if (
          acceptability.acceptMissionStatus !== AcceptMissionStatus.MissionEligible &&
          acceptability.acceptMissionStatus !== AcceptMissionStatus.IncompleteProfileRequirements &&
          !deferRelatedMinorRequirement
        ) {
          showUnavailableReferralMessage();
          return;
        }

        if (!referral.branchId || !referral.periodMissionConfigurationId) {
          showUnavailableReferralMessage();
          return;
        }

        apiMissions.config.accessToken = () => Promise.resolve(authToken);
        const missionDetails = await apiMissions.getMissionDetails(referral.branchId, null, false, undefined, undefined, apiMissions.tokenSource?.token);
        const referredMission = missionDetails?.missionDetails?.find(
          (mission) => mission.periodMissionConfigurationId === referral.periodMissionConfigurationId
        );
        const latitude = Number(referredMission?.geocoordinate?.y);
        const longitude = Number(referredMission?.geocoordinate?.x);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          showUnavailableReferralMessage();
          return;
        }

        // Persist before showing the popup: dismissing it must not erase the invitation.
        if (!referralScope || !referredMission) return;
        const remembered = await missionReferralRegistry.remember(referralScope, {
          referralId: referral.missionReferralId,
          token,
          branchId: referral.branchId,
          periodMissionConfigurationId: referral.periodMissionConfigurationId,
          expiresAt: referral.expiresAt.toISOString(),
          openedAt: Date.now(),
          promotionDataUpdatedAt: Date.now(),
          listing: referralListing(referredMission, referral, acceptability.canAccept),
        });
        if (!remembered) return;
        showReferralPopup(
          {
            token,
            branchId: referral.branchId,
            periodMissionConfigurationId: referral.periodMissionConfigurationId,
            latitude,
            longitude,
          },
          getFirstName(referral.senderMysteryShopperName)
        );
      } catch {
        showReferralMessage('mission-referral-error-title', 'mission-referral-error-body');
      }
    },
    [
      getReferralAcceptability,
      referralScope,
      getReferralDetails,
      showAgeRestrictionReferralMessage,
      showAlreadyAcceptedReferralMessage,
      showReferralMessage,
      showReferralPopup,
      showUnavailableReferralMessage,
    ]
  );

  const prepareReferralLogin = useCallback(
    (token: string) => {
      dispatch(authActions.setSignUpSource(MISSION_REFERRAL_SIGN_UP_SOURCE));
      dispatch(authActions.setReturnPath('missionReferral'));
      dispatch(authActions.setParams({ token }));
      setShowReferralLogin(true);
      setProcessing(false);
    },
    [dispatch]
  );

  const runPublicReferralAvailabilityCheck = useCallback(
    async (token: string) => {
      setProcessing(true);

      try {
        const referral = await getReferralDetails(token, true);

        if (!canUsePublicReferralCheckRef.current) {
          return;
        }

        if (isMissionReferralAccepted(referral)) {
          showAlreadyAcceptedReferralMessage();
          return;
        }

        if (!referral || referral.canAccept !== true || referral.isExpired === true || referral.status === MissionReferralStatus.InvitationCanceled) {
          showUnavailableReferralMessage();
          return;
        }

        setReferralLoginFirstName(getFirstName(referral.senderMysteryShopperName));
        setReferralAvailabilityChecked(true);
        prepareReferralLogin(token);
      } catch {
        if (!canUsePublicReferralCheckRef.current) {
          return;
        }

        showReferralMessage('mission-referral-error-title', 'mission-referral-error-body');
      }
    },
    [getReferralDetails, prepareReferralLogin, showAlreadyAcceptedReferralMessage, showReferralMessage, showUnavailableReferralMessage]
  );

  useEffect(() => {
    if (!referralToken) {
      if (processedTokenRef.current !== 'missing') {
        processedTokenRef.current = 'missing';
        showReferralMessage('mission-referral-error-title', 'mission-referral-error-body');
      }
      return;
    }

    if (authStore.logged === undefined) {
      return;
    }

    if (authStore.logged === LoggedState.NotLogged) {
      if (hasSignedInUser || authStore.idToken) {
        setShowReferralLogin(false);
        return;
      }

      if (showReferralLogin) {
        return;
      }

      if (publicAvailabilityTokenRef.current === referralToken) {
        return;
      }

      publicAvailabilityTokenRef.current = referralToken;
      void runPublicReferralAvailabilityCheck(referralToken);
      return;
    }

    publicAvailabilityTokenRef.current = undefined;

    if (authStore.logged === LoggedState.Anonymous) {
      dispatch(authActions.logout());
      dispatch(authActions.setSignUpSource(MISSION_REFERRAL_SIGN_UP_SOURCE));
      dispatch(authActions.setReturnPath('missionReferral'));
      dispatch(authActions.setParams({ token: referralToken }));
      return;
    }

    if (!isAuthenticatedReferralUser) {
      return;
    }

    setShowReferralLogin(false);

    if (shopperBootstrap === 'pending') {
      return;
    }

    if (processedTokenRef.current === referralToken) {
      return;
    }

    processedTokenRef.current = referralToken;
    if (shopperBootstrap === 'failed') {
      showReferralMessage('mission-referral-error-title', 'mission-referral-error-body');
      return;
    }
    void runReferralFlow(referralToken, authenticatedReferralToken);
  }, [
    authenticatedReferralToken,
    authStore.idToken,
    authStore.logged,
    shopperBootstrap,
    dispatch,
    hasSignedInUser,
    isAuthenticatedReferralUser,
    referralToken,
    runReferralFlow,
    runPublicReferralAvailabilityCheck,
    showReferralLogin,
    showReferralMessage,
  ]);

  if (showReferralLogin && canUsePublicReferralCheck) {
    return <Login route={referralLoginRoute} />;
  }

  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: loadingBackgroundColor }}>
      {processing && <Loading />}
    </View>
  );
};

export default MissionReferralAcceptance;
